import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../components/auth/AuthContext';
import { CATEGORIES } from '../data/scenarios';

export default function ScenarioBuilder() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('learning');
  const [icon, setIcon] = useState('📋');
  const [color, setColor] = useState('#667eea');
  const [instruction, setInstruction] = useState('');
  const [prompt, setPrompt] = useState('');
  const [outputFormat, setOutputFormat] = useState('');
  const [myScenarios, setMyScenarios] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadMyScenarios();
  }, []);

  const loadMyScenarios = async () => {
    try {
      const response = await fetch('/api/snap/custom-scenario?user_id=' + user.user_id, { credentials: 'include' });
      const result = await response.json();
      if (result.success) {
        setMyScenarios(result.data || []);
      }
    } catch (error) {
      console.error('Load scenarios error:', error);
    }
  };

  const handleSave = async () => {
    if (!name.trim() || !prompt.trim()) {
      toast.warning('시나리오 이름과 분석 프롬프트는 필수입니다.');
      return;
    }

    setSaving(true);
    try {
      const scenarioData = {
        user_id: user.user_id,
        name: name.trim(),
        description: description.trim(),
        category,
        icon,
        color,
        capture_instruction: instruction.trim() || `${name} 이미지를 촬영해주세요`,
        analyzer_prompt: prompt.trim(),
        output_format: outputFormat.trim(),
      };

      const response = await fetch('/api/snap/custom-scenario', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scenarioData),
      });

      const result = await response.json();
      if (result.success) {
        toast.success('커스텀 시나리오가 저장되었습니다!');
        setName('');
        setDescription('');
        setPrompt('');
        setInstruction('');
        setOutputFormat('');
        loadMyScenarios();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast.error('저장 실패: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const ICON_OPTIONS = ['📋', '📊', '🔬', '🏠', '🚗', '🍳', '🌱', '🎨', '📐', '⚡'];
  const COLOR_OPTIONS = ['#667eea', '#764ba2', '#e91e63', '#00bcd4', '#2ecc71', '#e67e22', '#e74c3c', '#9b59b6'];

  return (
    <div className="page-with-nav" style={{
      minHeight: '100vh',
      background: 'var(--gradient-bg)',
      padding: 'var(--space-lg)',
    }}>
      <div style={{ maxWidth: 'var(--max-width)', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: 'var(--space-lg)',
          animation: 'fadeInDown 0.4s ease forwards',
        }}>
          <button onClick={() => navigate('/')} style={{
            background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'white',
          }}>←</button>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', color: 'white', margin: 0 }}>
            🛠️ 시나리오 빌더
          </h1>
        </div>

        {/* Builder Form */}
        <div style={{
          background: 'white',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-lg)',
          boxShadow: 'var(--shadow-lg)',
          marginBottom: 'var(--space-xl)',
          animation: 'fadeInUp 0.4s ease forwards',
        }}>
          <h2 style={{ margin: '0 0 var(--space-md)', color: 'var(--color-text)', fontSize: 'var(--font-size-lg)' }}>
            새 시나리오 만들기
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            {/* 이름 */}
            <div>
              <label style={labelStyle}>시나리오 이름 *</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="예: 인테리어 시공 점검" style={inputStyle} />
            </div>

            {/* 설명 */}
            <div>
              <label style={labelStyle}>설명</label>
              <input type="text" value={description} onChange={e => setDescription(e.target.value)}
                placeholder="시나리오에 대한 간단한 설명" style={inputStyle} />
            </div>

            {/* 카테고리 */}
            <div>
              <label style={labelStyle}>카테고리</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {Object.entries(CATEGORIES).map(([key, cat]) => (
                  <button key={key} onClick={() => setCategory(key)} style={{
                    padding: '0.4rem 1rem',
                    border: category === key ? '2px solid var(--color-accent)' : '1px solid #ddd',
                    borderRadius: 'var(--radius-full)',
                    background: category === key ? 'var(--color-accent)' : 'white',
                    color: category === key ? 'white' : 'var(--color-text)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)',
                  }}>
                    {cat.icon} {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 아이콘 + 색상 */}
            <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>아이콘</label>
                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                  {ICON_OPTIONS.map(ic => (
                    <button key={ic} onClick={() => setIcon(ic)} style={{
                      width: '36px', height: '36px',
                      border: icon === ic ? '2px solid var(--color-accent)' : '1px solid #ddd',
                      borderRadius: 'var(--radius-md)',
                      background: icon === ic ? '#eef' : 'white',
                      cursor: 'pointer', fontSize: '1.2rem',
                    }}>{ic}</button>
                  ))}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>색상</label>
                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                  {COLOR_OPTIONS.map(c => (
                    <button key={c} onClick={() => setColor(c)} style={{
                      width: '36px', height: '36px',
                      border: color === c ? '3px solid #333' : '1px solid #ddd',
                      borderRadius: '50%',
                      background: c,
                      cursor: 'pointer',
                    }} />
                  ))}
                </div>
              </div>
            </div>

            {/* 촬영 안내 */}
            <div>
              <label style={labelStyle}>촬영 안내 문구</label>
              <input type="text" value={instruction} onChange={e => setInstruction(e.target.value)}
                placeholder="예: 시공 상태를 촬영해주세요" style={inputStyle} />
            </div>

            {/* 분석 프롬프트 */}
            <div>
              <label style={labelStyle}>AI 분석 프롬프트 *</label>
              <textarea value={prompt} onChange={e => setPrompt(e.target.value)}
                placeholder="AI에게 보낼 분석 지시문을 작성하세요.&#10;&#10;예: 이 이미지를 분석하여 다음 JSON 형식으로 응답해주세요:&#10;{&#10;  &quot;description&quot;: &quot;전체 설명&quot;,&#10;  &quot;issues_found&quot;: [&quot;문제점 목록&quot;],&#10;  &quot;recommendations&quot;: [&quot;권장사항 목록&quot;]&#10;}"
                style={{ ...inputStyle, minHeight: '150px', resize: 'vertical' }} />
            </div>

            {/* 출력 형식 */}
            <div>
              <label style={labelStyle}>원하는 출력 JSON 형식 (선택)</label>
              <textarea value={outputFormat} onChange={e => setOutputFormat(e.target.value)}
                placeholder='예: {"description": "", "score": 0, "issues": []}'
                style={{ ...inputStyle, minHeight: '80px', resize: 'vertical', fontFamily: 'monospace', fontSize: '0.85rem' }} />
            </div>

            {/* 미리보기 */}
            <div style={{
              padding: 'var(--space-md)',
              background: '#f8f9fa',
              borderRadius: 'var(--radius-lg)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
            }}>
              <span style={{ fontSize: '2rem' }}>{icon}</span>
              <div>
                <div style={{ fontWeight: 700, color }}>{name || '시나리오 이름'}</div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: '#999' }}>{description || '설명 없음'}</div>
              </div>
            </div>

            <button onClick={handleSave} disabled={saving} style={{
              padding: 'var(--space-md)',
              background: saving ? '#ccc' : 'var(--color-accent)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}>
              {saving ? '저장 중...' : '💾 시나리오 저장'}
            </button>
          </div>
        </div>

        {/* My Custom Scenarios */}
        {myScenarios.length > 0 && (
          <div>
            <h2 style={{ color: 'white', marginBottom: 'var(--space-md)', fontSize: 'var(--font-size-lg)' }}>
              내 커스텀 시나리오
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 'var(--space-md)',
            }}>
              {myScenarios.map((s, i) => (
                <div key={s.scenario_id} style={{
                  background: 'white',
                  borderRadius: 'var(--radius-xl)',
                  padding: 'var(--space-md)',
                  opacity: 0,
                  animation: 'fadeInUp 0.4s ease forwards',
                  animationDelay: `${i * 50}ms`,
                  cursor: 'pointer',
                }} onClick={() => navigate(`/snap/custom_${s.scenario_id}`)}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>{s.icon}</div>
                  <div style={{ fontWeight: 700, color: s.color || '#667eea' }}>{s.name}</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: '#999' }}>{s.description}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  marginBottom: '0.3rem',
  color: 'var(--color-text)',
};

const inputStyle = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};
